import { Component, DestroyRef } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { filter, finalize, mergeMap, switchMap, take, tap, timeout } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';

import { ManageSystemService } from '../../../../core/services/http-services/manage-system.service';
import { MonitorHub } from '../../../hubs/monitor.hub';
import { AndroidDeviceModel } from '../../../../core/models/entities/android-device.type';
import { MachineCategory } from '../../../../core/models/entities/machine-category.type';
import { AppConstants } from '../../../../app.constants';
import { ManageSystemMessagesEnum } from '../../../../core/enums/toastr-messages/manage-system-messages.enum';
import { ModalUpdateClientFilePathComponent } from '../modal-update-client-file-path/modal-update-client-file-path.component';
import { AdbService } from '../../../../wall-module/space-wall/shared/services/adb.service';
import { AdbAndroidIdentifier } from '../../../../core/models/view-models/adb-models/adb-android-identifier';
import { AdbDialogMessagesEnum } from '../../../../core/enums/toastr-messages/adb-dialog-messages.enum';
import { UtilsService } from '../../../../core/services/http-services/utils.service';

@Component({
  selector: 'modal-assign-android-device',
  templateUrl: './modal-assign-android-device.component.html',
  styleUrls: ['./modal-assign-android-device.component.scss']
})
export class ModalAssignAndroidDeviceComponent {
  androidDevice: AndroidDeviceModel = new AndroidDeviceModel();
  categories: MachineCategory[];
  isConnectionInProgress: boolean;
  ipAddress: string;
  ipAddressPattern = AppConstants.IP_ADDRESS_REGULAR;
  isApkInstalling: boolean;
  isConnectingToAdb: boolean;

  constructor(
    private readonly manageSystemService: ManageSystemService,
    private readonly dialogRef: MatDialogRef<ModalAssignAndroidDeviceComponent>,
    private readonly monitorHub: MonitorHub,
    private toastr: ToastrService,
    private readonly destroyRef: DestroyRef,
    private readonly dialog: MatDialog,
    private readonly adbService: AdbService,
    private readonly utilsService: UtilsService
  ) {}

  connect(): void {
    this.manageSystemService.connectDevice({
      ...this.androidDevice,
      Code: this.androidDevice.Code.toUpperCase(),
      CategoryId: this.androidDevice.CategoryId || null
    })
      .pipe(
        mergeMap(() => {
          this.isConnectionInProgress = true;
          this.dialogRef.updateSize(AppConstants.ANDROID_CONNECTION_MODAL_WINDOW_WIDTH);

          return this.monitorHub.deviceConnected;
        }),
        filter(value => !!value),
        timeout(AppConstants.ANDROID_CONNECTION_TIMEOUT),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        () => this.dialogRef.close(this.androidDevice.LocationName),
        () => {
          this.toastr.error(`${ManageSystemMessagesEnum.AgentAssignmentFailure}`);
          this.dialogRef.close();
        },
      );
  }

  connectToADB() {
    if (this.adbService.connectedIpAddresses.value.includes(this.ipAddress)) {
      this.toastr.info(AdbDialogMessagesEnum.DeviceAlreadyConnectedInfo);
      return;
    }

    this.isConnectingToAdb = true;
    this.toastr.success(AdbDialogMessagesEnum.DeviceConnectingSuccess);

    this.adbService.connectDeviceByServer(this.ipAddress).pipe(
      switchMap(() => this.adbService.connectedIpAddresses.pipe(take(1))),
      tap((ipAddresses) => ipAddresses.includes(this.ipAddress) ?
        this.toastr.success(AdbDialogMessagesEnum.DeviceConnectedSuccess) :
        this.toastr.error(AdbDialogMessagesEnum.DeviceConnectedError, AdbDialogMessagesEnum.DeviceConnectedErrorTitle)
      ),
      finalize(() => this.isConnectingToAdb = false)
    ).subscribe();
  }

  installApkFile() {
    this.utilsService.getApkUpdateFileName().pipe(
      filter(apkFileName => {
        !apkFileName && this.toastr.info(ManageSystemMessagesEnum.NoApkClientFileInfo);
        return !!apkFileName;
      }),
      switchMap(() => {
        const adbDevice = new AdbAndroidIdentifier(null, this.ipAddress);
        this.toastr.success(ManageSystemMessagesEnum.InstallApkClientSuccess);
        this.isApkInstalling = true;
        return this.adbService.updateAndroidAgent([adbDevice]);
      }),
      tap(() => this.toastr.success(ManageSystemMessagesEnum.InstalledApkClientSuccess)),
      finalize(() => this.isApkInstalling = false)
    ).subscribe();
  }

  updateSettings() {
    const dialogRef = this.dialog.open(ModalUpdateClientFilePathComponent, AppConstants.UPDATE_CLIENT_DIALOG_WINDOW_CONFIG);
    dialogRef.componentInstance.selectedTabIndex = AppConstants.ANDROID_UPDATE_CLIENT_TAB_INDEX;
  }
}
